import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ApiRequestStatus } from '../../../model/api-request';
import { GlobalActions } from '../../../model/global-actions';
import { ChangePhoneNumberState, getStatusUtenza } from '../../../store';
import {
  clearInsertPhoneNumberChangeSecondaryPhoneNumberError,
  resetChangeSecondaryPhoneNumberState,
  setConfirmNewPhoneNumberChangeSecondaryPhoneNumber,
  setPhoneNumberChangeSecondaryPhoneNumber,
  updateOpenDialogChangeSecondaryPhoneNumber,
} from '../../../store/features/profile/change-phone-number/change-phone-number-slice';
import { insertPhoneNumberChangeSecondaryPhoneNumber } from '../../../store/features/profile/change-phone-number/change-phone-number-thunk';
import { messagesForInsertPhoneNumberChangePhoneNumberError } from '../../../store/features/profile/change-phone-number/change-phone-number-utils';
import { useAppSelector } from '../../redux';

export interface ChangeSecondaryPhoneNumberDialogLogicCallbacks {
  phoneNumber: string;
  confirmNewPhoneNumber: string;
  insertPhoneNumberChangeSecondaryPhoneNumberRequestStatus: ApiRequestStatus;
  openDialogChangePhoneNumber: boolean;
  buttonDisabled: boolean;
  updatePhoneNumber: (value?: string) => void;
  updateConfirmNewPhoneNumberChangePhoneNumber: (value?: string) => void;
  handleSubmitInsertPhoneNumber: () => void;
  closeChangePhoneNumber: () => void;
  handleEditPhoneNumber: () => void;

  changeSecondaryPhoneDisabled: boolean;
}

export const useChangeSecondaryPhoneNumberDialogLogic = (
  actions: GlobalActions
): ChangeSecondaryPhoneNumberDialogLogicCallbacks => {
  const { showToast, closeToast } = actions;

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const {
    secondaryPhoneNumber,
    confirmNewSecondaryPhoneNumber,
    insertPhoneNumberChangeSecondaryPhoneNumberRequestStatus,
    insertPhoneNumberChangeSecondaryPhoneNumberError,
    openDialogChangeSecondaryPhoneNumber,
  }: ChangePhoneNumberState = useAppSelector(
    (state) => state.features.changePhoneNumber
  );

  const { changeContactsAvailable, documentiScaduti } = useAppSelector(
    (state) => state.data.user
  );

  const [buttonDisabled, setButtonDisabled] = useState(
    !secondaryPhoneNumber || secondaryPhoneNumber.length === 0
  );

  //Open dialog change phone number
  const handleEditPhoneNumber = useCallback(() => {
    dispatch(updateOpenDialogChangeSecondaryPhoneNumber(true));
  }, [dispatch]);

  //Reset state and close change phone number dialog
  const closeChangePhoneNumber = useCallback(() => {
    dispatch(resetChangeSecondaryPhoneNumberState());
  }, [dispatch]);

  // Save phone number
  const updatePhoneNumber = useCallback(
    (value: string) => {
      setButtonDisabled(false);
      dispatch(setPhoneNumberChangeSecondaryPhoneNumber(value));
    },
    [dispatch]
  );
  // Save new phone number
  const updateConfirmNewPhoneNumberChangePhoneNumber = useCallback(
    (value: string) => {
      setButtonDisabled(false);
      dispatch(setConfirmNewPhoneNumberChangeSecondaryPhoneNumber(value));
    },
    [dispatch]
  );

  //Submit insert phone number
  const handleSubmitInsertPhoneNumber = useCallback(() => {
    dispatch(insertPhoneNumberChangeSecondaryPhoneNumber());
  }, [dispatch]);

  useEffect(() => {
    if (
      insertPhoneNumberChangeSecondaryPhoneNumberRequestStatus === 'succeeded'
    ) {
      closeChangePhoneNumber();
      dispatch(getStatusUtenza());
    }
  }, [
    dispatch,
    closeChangePhoneNumber,
    insertPhoneNumberChangeSecondaryPhoneNumberRequestStatus,
  ]);

  useEffect(() => {
    if (insertPhoneNumberChangeSecondaryPhoneNumberError) {
      const errorMessages = messagesForInsertPhoneNumberChangePhoneNumberError(
        insertPhoneNumberChangeSecondaryPhoneNumberError
      );
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(clearInsertPhoneNumberChangeSecondaryPhoneNumberError());
    }
  }, [
    closeToast,
    dispatch,
    formatMessage,
    insertPhoneNumberChangeSecondaryPhoneNumberError,
    showToast,
  ]);

  const changeSecondaryPhoneDisabled = useMemo(() => {
    return Boolean(documentiScaduti || !changeContactsAvailable);
  }, [changeContactsAvailable, documentiScaduti]);

  return {
    phoneNumber: secondaryPhoneNumber,
    confirmNewPhoneNumber: confirmNewSecondaryPhoneNumber,
    insertPhoneNumberChangeSecondaryPhoneNumberRequestStatus,
    openDialogChangePhoneNumber: openDialogChangeSecondaryPhoneNumber,
    buttonDisabled,
    updatePhoneNumber,
    updateConfirmNewPhoneNumberChangePhoneNumber,
    handleSubmitInsertPhoneNumber,
    closeChangePhoneNumber,
    handleEditPhoneNumber,

    changeSecondaryPhoneDisabled,
  };
};
